import React, {useState} from 'react';
import { useForm } from "react-hook-form";
import Spinner from '../../components/Spinner';
import { AuthInput, PasswordInput } from '../../components/ui';
import { Auth } from '../../utils/amplifyWrapper';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import AuthSpritesImg from "../../assets/auth-sprites.svg"
import { useAuthStore } from '../../zustand-store/useAuthStore';

export default function ForgotPassword(){

    const [ isLoading, setIsLoading ] = useState(false);
    const forgotPasswordForm = useForm();
    const resetPasswordForm = useForm();
    const [formState, setFormState] = React.useState("forgotpassword");
    const [ username, setUsername ] = useState(null);
    const [ verificationCode, setVerificationCode ] = useState("");
    
    const { setAuthState: setGlobalAuthState, setUser: setGlobalUser } = useAuthStore();

    // Clear any stale auth state when component mounts
    React.useEffect(() => {
        setGlobalUser(null);
    }, [setGlobalUser]);

    // Cognito errors that are expected user-side conditions, not app bugs —
    // show a toast but DON'T report them to Sentry/Bugsink (they're just noise).
    const EXPECTED_AUTH_ERRORS = [
        'UserNotFoundException',      // no account for that email
        'InvalidParameterException',  // account has no verified email/phone to reset (e.g. Google sign-up)
        'LimitExceededException',     // too many attempts — user just needs to wait
    ];

    const handleForgotPasswordError = (error) => {
        if (error?.name === 'InvalidParameterException') {
            // Typically a Google-federated account: no verified email/password to reset.
            toast.error("This account doesn't have a verified email to send a reset code to. If you signed up with Google, please use \"Continue with Google\" to sign in.");
        } else {
            toast.error(error?.message || 'Something went wrong. Please try again.');
        }

        if (!EXPECTED_AUTH_ERRORS.includes(error?.name)) {
            Sentry.captureException(error);
        }
    };

    const onSubmitForgotPasswordForm = async data => {

        const { username } = data;
        setUsername(username);
        setIsLoading(true);

        Auth.forgotPassword(username)
        .then(() => {

            setFormState("verifycode")

        }).catch(handleForgotPasswordError).finally(()=>{

            setIsLoading(false);

        });

    };

    const onResendCode = async () =>{

        Auth.forgotPassword(username)
        .then(() => {

            toast.success("Code has been resent successfully.");

        }).catch(handleForgotPasswordError).finally(()=>{

            setIsLoading(false);

        });
    }

    const onSubmitVerificationCodeForm = async data => {
        const { verificationCode } = data;
        setVerificationCode(verificationCode);
        setFormState("resetpassword")
    };

    const onSubmitResetPasswordForm = async data => {

        const { password, password1 } = data;
        if(password !== password1){
            return toast.error("Both passwords do not match");
        }

        setIsLoading(true);
        Auth.forgotPasswordSubmit(username, verificationCode, password)
        .then(() => {
            toast.success("Password has been reset successfully.");
            setGlobalAuthState("signin");
        })
        .catch(error => {
            toast.error(error?.message || 'Something went wrong. Please try again.');
            // Wrong/expired code or weak password are expected user errors — not app bugs.
            const expected = ['CodeMismatchException', 'ExpiredCodeException', 'InvalidPasswordException', 'LimitExceededException'];
            if (!expected.includes(error?.name)) {
                Sentry.captureException(error);
            }
            // Keep the user at the most relevant step instead of restarting the whole flow.
            if (error?.name === 'InvalidPasswordException') {
                // Weak password — let them fix it without re-entering the code.
                setFormState("resetpassword");
            } else if (error?.name === 'CodeMismatchException' || error?.name === 'ExpiredCodeException') {
                // Bad/expired code — send them back to re-enter or resend the code.
                setFormState("verifycode");
            } else {
                setFormState("forgotpassword");
            }
        }).finally(()=>{
            setIsLoading(false)
        });


    }

    return(
        <div className="row my-3 my-md-0">
            <div className="col-md-8 offset-md-2 col-lg-6 offset-lg-3 px-0">
                {
                    (formState === "forgotpassword") &&
                    <form onSubmit={forgotPasswordForm.handleSubmit(onSubmitForgotPasswordForm)} >
                        <h4 className="fw-bold text-body-emphasis text-center mb-1">Recover Password</h4>
                        <p className="text-center mb-4" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>
                            Enter your registered email address and we'll send you a reset code.
                        </p>
                        <AuthInput
                            label="Email address"
                            name="username"
                            type="email"
                            register={forgotPasswordForm.register}
                            errors={forgotPasswordForm.formState.errors.username}
                            validation={{ required: true, pattern: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/g }}
                            backgroundImage={AuthSpritesImg}
                            backgroundPosition="4px -35px"
                        />
                        <button
                            type={isLoading ? "button" : "submit"}
                            className="btn btn-primary w-100"
                            style={{ height: 44 }}
                        >
                            {isLoading ? <Spinner color="white" /> : 'Send Code'}
                        </button>
                        <p className="text-primary text-center mt-3" style={{cursor: 'pointer', fontSize: 13}} onClick={()=>{setGlobalAuthState("signin")}}>Back to Sign In</p>
                    </form>
                }
                {
                    (formState === "verifycode") &&
                    <form className="mt-5" onSubmit={resetPasswordForm.handleSubmit(onSubmitVerificationCodeForm)}>
                        <h4 className="fw-bold text-body-emphasis text-center mb-1">Enter Verification Code</h4>
                        <p className="text-center mb-4" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>
                            Please enter the verification code sent to your email.
                        </p>
                        <AuthInput
                            label="Please enter the code below"
                            name="verificationCode"
                            type="text"
                            register={resetPasswordForm.register}
                            errors={resetPasswordForm.formState.errors.verificationCode}
                            validation={{ required: true }}
                            backgroundImage={AuthSpritesImg}
                            backgroundPosition="4px -70px"
                        />
                        <button type="submit" className="btn btn-primary w-100" style={{ height: 44 }}>Proceed</button>
                        <p className="text-center mt-3" style={{ fontSize: 13, color: 'var(--bs-secondary-color)' }}>
                            Haven't received the code? Check your spam folder or{' '}
                            <span className="text-primary" style={{cursor: 'pointer'}} onClick={()=>{onResendCode()}}>click here to resend</span> the code.
                        </p>
                    </form>
                }
                {
                    (formState === "resetpassword") &&
                    <form className="mt-5" onSubmit={resetPasswordForm.handleSubmit(onSubmitResetPasswordForm)}>
                        <h4 className="fw-bold text-body-emphasis text-center mb-1">Reset Password</h4>
                        <p className="text-center mb-4" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>Set a new password below.</p>
                        <PasswordInput
                            label="Password (min 8 characters)"
                            name="password"
                            register={resetPasswordForm.register}
                            errors={resetPasswordForm.formState.errors.password}
                            validation={{ required: true, minLength: 8 }}
                            backgroundImage={AuthSpritesImg}
                            backgroundPosition="4px -70px"
                        />
                        <PasswordInput
                            label="Retype Password"
                            name="password1"
                            register={resetPasswordForm.register}
                            errors={resetPasswordForm.formState.errors.password1}
                            validation={{ required: true, minLength: 8 }}
                            backgroundImage={AuthSpritesImg}
                            backgroundPosition="4px -70px"
                        />
                        <button
                            type={isLoading ? "button" : "submit"}
                            className="btn btn-primary w-100"
                            style={{ height: 44 }}
                        >
                            {isLoading ? <Spinner color="white" /> : 'Reset Password'}
                        </button>
                        <p className="mt-3" style={{ fontSize: 13, color: 'var(--bs-secondary-color)' }}>
                            Haven't received the code? Check your spam folder or{' '}
                            <span className="text-primary" style={{cursor: 'pointer'}} onClick={()=>{onResendCode()}}>click here to resend.</span>
                        </p>
                    </form>
                }
            </div>
        </div>
    );

}
